import React from "react";
import { ReactQuestionFactory } from "survey-react-ui";
import { QuestionNonValue, Serializer, ElementFactory } from "survey-core";
import { Tip } from "design-system/src/components/tip";

export class TipQuestionModel extends QuestionNonValue {
  static type = "tip";

  getType() {
    return TipQuestionModel.type;
  }

  get variant(): "tip" | "callout" {
    return this.getPropertyValue("variant", "tip");
  }

  set variant(val: "tip" | "callout") {
    this.setPropertyValue("variant", val);
  }

  get heading(): string {
    return this.getPropertyValue("heading");
  }

  set heading(val: string) {
    this.setPropertyValue("heading", val);
  }

  get content(): string {
    return this.getPropertyValue("content", "");
  }

  set content(val: string) {
    this.setPropertyValue("content", val);
  }

  get linkHref(): string {
    return this.getPropertyValue("linkHref");
  }

  set linkHref(val: string) {
    this.setPropertyValue("linkHref", val);
  }

  get linkText(): string {
    return this.getPropertyValue("linkText");
  }

  set linkText(val: string) {
    this.setPropertyValue("linkText", val);
  }

  public get name(): string {
    return this.getPropertyValue("name");
  }

  public set name(val: string) {
    this.setPropertyValue("name", val);
  }
}

interface TipQuestionComponentProps {
  question: TipQuestionModel;
}

export function TipQuestionComponent({ question }: TipQuestionComponentProps) {
  return (
    <div className={question.cssClasses?.root}>
      <Tip variant={question.variant || "tip"}>
        {question.heading && <Tip.Heading>{question.heading}</Tip.Heading>}
        <Tip.Caption>{question.content}</Tip.Caption>
        {question.linkHref && question.linkText && (
          <Tip.Link href={question.linkHref} data-testid="tip-question-link">
            {question.linkText}
          </Tip.Link>
        )}
      </Tip>
    </div>
  );
}

export function registerTipQuestion() {
  ElementFactory.Instance.registerElement(
    "tip",
    (name: string) => new TipQuestionModel(name),
  );

  Serializer.addClass(
    "tip",
    [
      {
        name: "variant",
        category: "general",
        displayName: "Variant",
        type: "dropdown",
        default: "tip",
        choices: [
          { value: "tip", text: "Tip (lightbulb)" },
          { value: "callout", text: "Callout (info circle)" },
        ],
      },
      {
        name: "heading",
        category: "general",
        displayName: "Heading",
        type: "string",
      },
      {
        name: "content",
        category: "general",
        displayName: "Content",
        type: "text",
        isRequired: true,
      },
      {
        name: "linkHref",
        category: "general",
        displayName: "Link URL",
        type: "string",
      },
      {
        name: "linkText",
        category: "general",
        displayName: "Link Text",
        type: "string",
        visibleIf(obj: TipQuestionModel) {
          return !!obj.linkHref;
        },
      },
    ],
    () => new TipQuestionModel(""),
    "nonvalue",
  );

  ReactQuestionFactory.Instance.registerQuestion(
    TipQuestionModel.type,
    (propsOrName: string | TipQuestionComponentProps) => {
      const props =
        typeof propsOrName === "string"
          ? { question: new TipQuestionModel(propsOrName) }
          : propsOrName;
      return React.createElement(TipQuestionComponent, props);
    },
  );
}

export function createTipQuestion(config: {
  name: string;
  variant?: "tip" | "callout";
  heading?: string;
  content: string;
  link?: {
    href: string;
    text: string;
  };
}) {
  return {
    type: "tip",
    name: config.name,
    variant: config.variant || "tip",
    heading: config.heading,
    content: config.content,
    linkHref: config.link?.href,
    linkText: config.link?.text,
  };
}
